// webapp/src/AppHeader.js
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiUser, FiLogOut, FiChevronDown, FiSettings, FiBarChart2, FiCalendar, FiActivity, FiHome } from 'react-icons/fi';
import { useScreenBuilder } from './ScreenBuilderContext';
import config_names from './config_names';

function AppHeader({ currentUser, onSignOut }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { resetScreens } = useScreenBuilder();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navItems = [
    { path: '/dashboard', label: 'Home', icon: FiHome },
    { path: '/dashboard/strategies', label: 'Strategy', icon: FiSettings },
    { path: '/dashboard/charts', label: 'Chart', icon: FiActivity },
    { path: '/dashboard/seasonality', label: 'Seasonality', icon: FiCalendar }
  ];

  return (
    <header className="app-header">
      <div className="header-logo">
        <Link to="/dashboard" className="logo-link">
          <FiBarChart2 className="logo-icon" />
          <span>{config_names.projectName}</span>
        </Link>
      </div>
      
      {currentUser ? (
        <>
          <nav className="header-nav">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isDashboardRoot = location.pathname === '/dashboard' || location.pathname === '/dashboard/';
              const isActive = item.path === '/dashboard'
                ? isDashboardRoot
                : location.pathname === item.path;

              // Special handling for Strategy to reset builder view
              if (item.path === '/dashboard/strategies') {
                const handleScreensClick = () => {
                  // If already on strategies page, reset the builder view
                  if (isActive) {
                    resetScreens();
                  } else {
                    // Otherwise navigate to strategies
                    navigate(item.path);
                  }
                };

                return (
                  <button
                    key={item.path}
                    onClick={handleScreensClick}
                    className={`nav-item ${isActive ? 'active' : ''}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <Icon size={18} />
                    <span>{item.label}</span>
                  </button>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive ? 'active' : ''}`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
          
          <div className="user-controls" ref={dropdownRef}>
            <div 
              className="user-info-button"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <div className="user-info">
                <FiUser />
                <span>{currentUser.email}</span>
                <FiChevronDown size={16} />
              </div>
            </div>
            
            {showDropdown && (
              <div className="user-dropdown">
                <Link to="/dashboard/profile" className="dropdown-item" onClick={() => setShowDropdown(false)}>
                  <FiUser />
                  <span>Profile</span>
                </Link>
                <button className="dropdown-item" onClick={onSignOut}>
                  <FiLogOut />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="auth-controls">
          <Link to="/login" className="secondary-button">
            Log In
          </Link>
          <Link to="/signup" className="primary-button">
            Sign Up
          </Link>
        </div>
      )}
    </header>
  );
}

export default AppHeader;
